/**
 * A character offset in the STORED transcript text → the second of the call
 * it falls on.
 *
 * An annotation is stored as `startPosition`/`endPosition` in characters of
 * the stored transcript text, not as a timecode. The coordinate system is the
 * one `TranscriptViewer` (ui-worker) builds when it saves and highlights an
 * annotation: segments of the diarization one after another, each followed by
 * one separator — the running offset of segment `i` is the sum of
 * `text.length + 1` over the segments before it (a missing segment adds 0).
 * `transcriptCharOffsets` is that rule in one place: `TranscriptViewer` and
 * `transcriptOffsetToSeconds` both call it, so the minute a notification links
 * to cannot drift from the moment the annotation marks.
 *
 * `start` is in SECONDS, as the diarization is stored in Postgres (the
 * transcript route converts it to milliseconds only for the UI).
 */
export interface TranscriptOffsetSegment {
  start: number;
  text?: string | null;
}

/**
 * Where each segment begins in the stored transcript text: entry `i` is the sum
 * of `text.length + 1` over the segments before `i`. A missing (null/undefined)
 * segment adds nothing; a segment with no text counts as an empty one — its
 * separator only.
 */
export function transcriptCharOffsets(
  segments: readonly ({ text?: string | null } | null | undefined)[]
): number[] {
  const offsets = new Array<number>(segments.length);
  let offset = 0;
  for (let i = 0; i < segments.length; i++) {
    offsets[i] = offset;
    const segment = segments[i];
    if (segment) {
      offset += (segment.text ?? '').length + 1;
    }
  }
  return offsets;
}

/**
 * The start second (floored) of the segment the offset falls in; the separator
 * after a segment belongs to it. `null` — no segments, an offset outside the
 * text, or a segment without a usable start.
 */
export function transcriptOffsetToSeconds(
  segments: readonly (TranscriptOffsetSegment | null | undefined)[] | null | undefined,
  offset: number | null | undefined
): number | null {
  if (!segments || segments.length === 0) return null;
  if (typeof offset !== 'number' || !Number.isInteger(offset) || offset < 0) return null;

  const offsets = transcriptCharOffsets(segments);
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment) continue;
    const segmentEnd = (offsets[i] ?? 0) + (segment.text ?? '').length + 1;
    if (offset < segmentEnd) {
      const start = segment.start;
      return typeof start === 'number' && isFinite(start) && start >= 0
        ? Math.floor(start)
        : null;
    }
  }
  return null;
}
